import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/ui/BackButton';
import { taskPhotoService } from '../../services/taskPhotoService';
import { useAuth } from '../../contexts/AuthContext';
import MobilePhotoInput from '../../components/ui/MobilePhotoInput';

const CameraFirstPhotoUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [stream, setStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const siteId = location?.state?.siteId;
  const siteName = location?.state?.siteName;

  useEffect(() => {
    if (userProfile?.role !== 'worker') {
      navigate('/admin-dashboard', { replace: true });
      return;
    }

    if (!siteId) {
      setError('No site selected. Please return to home and try again.');
      return;
    }

    startCamera();

    return () => {
      stopCamera();
    };
  }, [userProfile, siteId, navigate]);

  const startCamera = async () => {
    try {
      setError('');
      const mediaStream = await navigator?.mediaDevices?.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      setStream(mediaStream);
      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraActive(true);
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Unable to access camera. Please check permissions or upload a photo instead.');
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef?.current || !canvasRef?.current) return;

    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    const context = canvas?.getContext('2d');

    canvas.width = video?.videoWidth;
    canvas.height = video?.videoHeight;
    context?.drawImage(video, 0, 0, canvas?.width, canvas?.height);

    canvas?.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `daily-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const photoUrl = URL.createObjectURL(blob);
        setCapturedPhoto({ file, url: photoUrl });
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file && file?.type?.startsWith('image/')) {
      const photoUrl = URL.createObjectURL(file);
      setCapturedPhoto({ file, url: photoUrl });
      stopCamera();
      setError('');
    } else {
      setError('Please select a valid image file');
    }
  };

  const retakePhoto = () => {
    if (capturedPhoto?.url) {
      URL.revokeObjectURL(capturedPhoto?.url);
    }
    setCapturedPhoto(null);
    setCaption('');
    startCamera();
  };

  const handleUpload = async () => {
    if (!capturedPhoto?.file) {
      setError('Please capture or select a photo first');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const result = await taskPhotoService?.uploadPhoto(
        capturedPhoto?.file,
        null,
        siteId,
        caption
      );

      if (result?.error) throw result.error;

      if (capturedPhoto?.url) {
        URL.revokeObjectURL(capturedPhoto?.url);
      }

      navigate('/home', { state: { message: 'Photo uploaded successfully' } });
    } catch (err) {
      console.error('[CameraPhotoUpload] upload photo failed:', { code: err?.code, message: err?.message });
      setError(err?.message ? `Failed to upload photo: ${err.message}` : 'Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (capturedPhoto?.url) {
      URL.revokeObjectURL(capturedPhoto?.url);
    }
    stopCamera();
    navigate('/home');
  };

  return (
    <>
      <Helmet>
        <title>Add Photo - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="mb-4">
              <BackButton fallback="/home" />
              <h1 className="text-2xl font-bold text-foreground mb-1">Add Photo</h1>
              {siteName && (
                <p className="text-muted-foreground text-sm">Documenting work at {siteName}</p>
              )}
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
              {!capturedPhoto ? (
                <div className="space-y-4 p-4 sm:p-6">
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    {!cameraActive && (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted">
                        <div className="text-center">
                          <Icon name="Camera" size={48} className="text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Camera not available</p>
                        </div>
                      </div>
                    )}
                    {cameraActive && (
                      <button
                        onClick={capturePhoto}
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-white border-4 border-accent shadow-lg flex items-center justify-center active:scale-95 transition-transform"
                        aria-label="Capture photo"
                      >
                        <Icon name="Camera" size={28} className="text-accent" />
                      </button>
                    )}
                  </div>

                  <canvas ref={canvasRef} className="hidden" />

                  <div className="flex flex-col gap-3">
                    {cameraActive && (
                      <Button
                        onClick={capturePhoto}
                        size="lg"
                        className="w-full min-h-[56px] bg-accent hover:bg-accent/90 text-white text-base font-semibold"
                      >
                        <Icon name="Camera" size={24} />
                        <span className="ml-2">Capture Photo</span>
                      </Button>
                    )}
                    {/* Mobile-friendly: separate Take Photo / Choose from Gallery */}
                    <MobilePhotoInput
                      onFilesSelected={(files) => {
                        const file = files?.[0];
                        if (file) {
                          const photoUrl = URL.createObjectURL(file);
                          setCapturedPhoto({ file, url: photoUrl });
                          stopCamera();
                          setError('');
                        }
                      }}
                      label="Upload Photo"
                      hint="Take a photo or choose from gallery"
                    />
                  </div>

                  <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={18} className="text-accent mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">Photo Guidelines:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Ensure good lighting for clear documentation</li>
                          <li>Capture the full context of the work area</li>
                          <li>Photos are saved to the site photo gallery</li>
                          <li>Add a caption to provide context</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 p-4 sm:p-6">
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <img
                      src={capturedPhoto?.url}
                      alt="Captured site photo"
                      className="w-full h-auto"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Caption (Optional)
                    </label>
                    <textarea
                      value={caption}
                      onChange={(e) => setCaption(e?.target?.value)}
                      placeholder="Add a description of what this photo shows..."
                      rows={3}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[80px]"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={retakePhoto}
                      variant="outline"
                      size="lg"
                      className="flex-1 min-h-[56px] text-base font-semibold"
                      disabled={uploading}
                    >
                      <Icon name="RotateCcw" size={22} />
                      <span className="ml-2">Retake</span>
                    </Button>
                    <Button
                      onClick={handleUpload}
                      size="lg"
                      className="flex-1 min-h-[56px] bg-success hover:bg-success/90 text-white text-base font-semibold"
                      loading={uploading}
                      disabled={uploading}
                    >
                      <Icon name="Upload" size={22} />
                      <span className="ml-2">{uploading ? 'Uploading...' : 'Upload Photo'}</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CameraFirstPhotoUpload;